import { defineStore } from "pinia";
import { ref } from "vue";
import type { Note, CreateNoteInput, UpdateNoteInput } from "@shared/types";
import { WSEvent } from "@shared/constants";
import { api } from "@/utils/api";
import type { Socket } from "socket.io-client";

export const useNotesStore = defineStore("notes", () => {
  // State
  const notes = ref<Note[]>([]);
  const resultNotes = ref<Map<number, Note[]>>(new Map());
  const isLoading = ref(false);

  // Actions

  /**
   * Fetch all notes by current user
   */
  const fetchUserNotes = async () => {
    try {
      isLoading.value = true;
      const data = await api.get<Note[]>("/api/notes/my");
      notes.value = data;
    } catch (error) {
      console.error("Failed to fetch user notes:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch notes for a result
   */
  const fetchNotesForResult = async (resultId: number) => {
    try {
      const data = await api.get<Note[]>(`/api/notes/result/${resultId}`);
      resultNotes.value.set(resultId, data);
      return data;
    } catch (error) {
      console.error("Failed to fetch notes for result:", error);
      throw error;
    }
  };

  /**
   * Create a new note
   */
  const createNote = async (input: CreateNoteInput) => {
    try {
      const note = await api.post<Note>("/api/notes", input);
      notes.value.unshift(note);

      // Update result notes
      const current = resultNotes.value.get(input.resultId) || [];
      resultNotes.value.set(input.resultId, [note, ...current]);

      return note;
    } catch (error) {
      console.error("Failed to create note:", error);
      throw error;
    }
  };

  /**
   * Update a note
   */
  const updateNote = async (id: number, input: UpdateNoteInput) => {
    try {
      const note = await api.put<Note>(`/api/notes/${id}`, input);

      // Update in user notes
      const index = notes.value.findIndex((n) => n.id === id);
      if (index !== -1) {
        notes.value[index] = note;
      }

      // Update in result notes
      const current = resultNotes.value.get(note.resultId) || [];
      const resultIndex = current.findIndex((n) => n.id === id);
      if (resultIndex !== -1) {
        current[resultIndex] = note;
        resultNotes.value.set(note.resultId, [...current]);
      }

      return note;
    } catch (error) {
      console.error("Failed to update note:", error);
      throw error;
    }
  };

  /**
   * Delete a note
   */
  const deleteNote = async (id: number) => {
    try {
      const note = notes.value.find((n) => n.id === id);
      await api.delete(`/api/notes/${id}`);

      // Remove from user notes
      notes.value = notes.value.filter((n) => n.id !== id);

      // Remove from result notes
      if (note) {
        const current = resultNotes.value.get(note.resultId) || [];
        resultNotes.value.set(
          note.resultId,
          current.filter((n) => n.id !== id),
        );
      }
    } catch (error) {
      console.error("Failed to delete note:", error);
      throw error;
    }
  };

  /**
   * Delete all notes for a result
   */
  const deleteNotesForResult = async (resultId: number) => {
    try {
      await api.delete(`/api/notes/result/${resultId}`);

      // Remove from user notes
      notes.value = notes.value.filter((n) => n.resultId !== resultId);

      // Clear result notes
      resultNotes.value.set(resultId, []);
    } catch (error) {
      console.error("Failed to delete notes for result:", error);
      throw error;
    }
  };

  /**
   * Get notes for a result (from cache or fetch)
   */
  const getNotesForResult = async (resultId: number): Promise<Note[]> => {
    if (resultNotes.value.has(resultId)) {
      return resultNotes.value.get(resultId)!;
    }
    return await fetchNotesForResult(resultId);
  };

  /**
   * Setup WebSocket listeners
   */
  const setupWebSocketListeners = (socket: Socket) => {
    socket.on(WSEvent.NOTE_CREATED, ({ note }: { note: Note }) => {
      if (!notes.value.find((n) => n.id === note.id)) {
        notes.value.unshift(note);
      }

      // Update result notes
      const current = resultNotes.value.get(note.resultId) || [];
      if (!current.find((n) => n.id === note.id)) {
        resultNotes.value.set(note.resultId, [note, ...current]);
      }
    });

    socket.on(WSEvent.NOTE_UPDATED, ({ note }: { note: Note }) => {
      // Update in user notes
      const index = notes.value.findIndex((n) => n.id === note.id);
      if (index !== -1) {
        notes.value[index] = note;
      }

      // Update in result notes
      const current = resultNotes.value.get(note.resultId) || [];
      const resultIndex = current.findIndex((n) => n.id === note.id);
      if (resultIndex !== -1) {
        current[resultIndex] = note;
        resultNotes.value.set(note.resultId, [...current]);
      }
    });

    socket.on(WSEvent.NOTE_DELETED, ({ noteId }: { noteId: number }) => {
      const note = notes.value.find((n) => n.id === noteId);

      // Remove from user notes
      notes.value = notes.value.filter((n) => n.id !== noteId);

      // Remove from result notes
      if (note) {
        const current = resultNotes.value.get(note.resultId) || [];
        resultNotes.value.set(
          note.resultId,
          current.filter((n) => n.id !== noteId),
        );
      }
    });

    socket.on(
      WSEvent.NOTES_CLEARED,
      ({ resultId, count }: { resultId: number; count: number }) => {
        // Remove from user notes
        notes.value = notes.value.filter((n) => n.resultId !== resultId);

        // Clear result notes
        resultNotes.value.set(resultId, []);
      },
    );
  };

  return {
    // State
    notes,
    resultNotes,
    isLoading,

    // Actions
    fetchUserNotes,
    fetchNotesForResult,
    createNote,
    updateNote,
    deleteNote,
    deleteNotesForResult,
    getNotesForResult,
    setupWebSocketListeners,
  };
});
